// Pages publiques : page de candidature d'une campagne, lien court, invitation d'un cotitulaire ou d'un garant.
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError, get, post } from "@core/api";
import { date, money } from "@core/format";
import { PublicShell } from "@core/layout";
import { useSession } from "@core/session";
import { Button, ErrorBox, Input, KeyValues, Loading, Modal, Notice, Select, Textarea, fieldError } from "@core/ui";
import { CHECKS_EXPLAINED, FORBIDDEN_PIECES, LEGAL_PIECES, regimeLabel } from "./shared";

type PublicCampaign = {
  campaign: {
    slug: string;
    title: string;
    status: string;
    status_label: string;
    accepting: boolean;
    description: string;
    lease_regime: string;
    rent_cents: number;
    charges_cents: number;
    charges_mode: string;
    deposit_cents: number;
    available_on: string | null;
    published_at: string | null;
  };
  property: { id: string; label: string; kind: string; city: string | null; postal_code: string | null; surface_m2: number | null; rooms: number | null; furnished: boolean; dpe_class: string | null };
  landlord: { name: string; kind: string };
  photos: { id: string; caption: string; url: string }[];
};

const LANDLORD_KIND: Record<string, string> = { particulier: "Bailleur particulier", sci: "SCI", indivision: "Indivision", professionnel: "Professionnel de l’immobilier" };

/** Lecture facultative d'un autre module : absente ou en erreur, la section ne s'affiche pas. */
function useOptional<T>(path: string | null) {
  return useQuery({ queryKey: ["facultatif", path], queryFn: () => get<T>(path!), enabled: !!path, retry: false, staleTime: 300_000 });
}

function textOf(v: any): string | null {
  if (!v) return null;
  if (typeof v === "string") return v;
  for (const k of ["label", "title", "text", "summary", "message", "status_label", "description"]) {
    if (typeof v[k] === "string" && v[k].trim()) return v[k];
  }
  return null;
}

/** Statut de vérification du bailleur fourni par le module patrimoine, s'il répond. */
function LandlordVerification({ propertyId }: { propertyId: string }) {
  const q = useOptional<any>(`/api/v1/public/properties/${propertyId}/landlord-verification`);
  const text = textOf(q.data);
  if (!text) return null;
  const details = Array.isArray(q.data?.items) ? q.data.items.map(textOf).filter(Boolean) : [];
  return (
    <p className="small">
      <strong>Vérification du bailleur :</strong> {text}
      {details.length ? <span className="block muted">{details.join(" · ")}</span> : null}
    </p>
  );
}

/** Critères de la campagne publiés par le module contrôle, s'il les expose. */
function PublicCriteria({ slug }: { slug: string }) {
  const q = useOptional<any>(`/api/v1/public/campaigns/${slug}/criteria`);
  const d = q.data;
  const list: string[] = Array.isArray(d?.items) ? d.items.map(textOf).filter(Boolean) : Array.isArray(d?.criteria) ? d.criteria.map(textOf).filter(Boolean) : Array.isArray(d) ? d.map(textOf).filter(Boolean) : [];
  const summary = typeof d?.summary === "string" ? d.summary : null;
  if (!list.length && !summary) return null;
  return (
    <section className="stack-sm">
      <h2>Critères fixés par le bailleur</h2>
      {summary ? <p>{summary}</p> : null}
      {list.length ? (
        <ul>
          {list.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      ) : null}
      <p className="small muted">Ces critères s’appliquent aux montants justifiés par vos pièces. Ils ne suffisent pas à décider : le bailleur examine chaque dossier.</p>
    </section>
  );
}

const REASONS = [
  { value: "", label: "Choisissez un motif" },
  { value: "annonce_trompeuse", label: "Annonce trompeuse ou inexacte" },
  { value: "logement_indisponible", label: "Logement déjà loué ou indisponible" },
  { value: "demande_abusive", label: "Demande de pièces ou de paiement abusive" },
  { value: "discrimination", label: "Critère discriminatoire" },
  { value: "autre", label: "Autre problème" },
];

function ReportModal({ slug, open, onClose }: { slug: string; open: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ reason: "", message: "", email: "" });
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Signaler cette annonce"
      footer={
        done ? (
          <Button onClick={onClose}>Fermer</Button>
        ) : (
          <>
            <Button variant="discret" onClick={onClose}>
              Annuler
            </Button>
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await post<{ message: string }>(`/api/v1/public/campaigns/${slug}/report`, { ...form, email: form.email || null });
                  setDone(r.message);
                } catch (err) {
                  setError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Envoyer le signalement
            </Button>
          </>
        )
      }
    >
      {done ? (
        <Notice tone="succes">{done}</Notice>
      ) : (
        <div className="stack">
          <ErrorBox error={error} />
          <Select label="Motif" value={form.reason} options={REASONS} error={fieldError(error, "reason")} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Textarea label="Précisions" rows={4} value={form.message} error={fieldError(error, "message")} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <Input label="Votre adresse e-mail (facultatif)" type="email" value={form.email} error={fieldError(error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} help="Seulement si vous acceptez d’être recontacté par l’équipe Bailivo." />
        </div>
      )}
    </Modal>
  );
}

export function PublicCampaignPage() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, loading: sessionLoading } = useSession();
  const q = useQuery({ queryKey: ["candidature-publique", slug], queryFn: () => get<PublicCampaign>(`/api/v1/public/campaigns/${slug}`), retry: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [report, setReport] = useState(false);
  const counted = useRef(false);
  const autoApplied = useRef(false);
  const here = `/candidature/${slug}`;

  // Vue anonyme, comptée une fois par affichage.
  useEffect(() => {
    if (!q.data || counted.current) return;
    counted.current = true;
    post(`/api/v1/public/campaigns/${slug}/views`, { channel: params.get("via") === "court" ? "lien_court" : "page" }).catch(() => undefined);
  }, [q.data, slug, params]);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ id: string; existing: boolean }>(`/api/v1/public/campaigns/${slug}/apply`, {});
      nav(`/moi/candidatures/${r.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // Retour après inscription ou connexion : la candidature est créée sans nouveau clic.
  useEffect(() => {
    if (autoApplied.current || sessionLoading || !user || !q.data?.campaign.accepting || params.get("deposer") !== "1") return;
    autoApplied.current = true;
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessionLoading, q.data, params]);

  if (q.isLoading) {
    return (
      <PublicShell wide>
        <Loading />
      </PublicShell>
    );
  }
  if (q.error || !q.data) {
    return (
      <PublicShell>
        <h1>Annonce introuvable</h1>
        <p>{q.error instanceof ApiError ? q.error.message : "Cette annonce n’existe pas ou n’est plus publiée."}</p>
        <Link to="/">Revenir à l’accueil</Link>
      </PublicShell>
    );
  }
  const { campaign: c, property: p, landlord, photos } = q.data;
  const suite = encodeURIComponent(`${here}?deposer=1`);
  const place = [p.postal_code, p.city].filter(Boolean).join(" ");
  const facts: [string, string][] = [
    ["Loyer hors charges", `${money(c.rent_cents)} par mois`],
    ["Charges", c.charges_cents ? `${money(c.charges_cents)} par mois (${c.charges_mode === "forfait" ? "forfait" : "provision régularisée chaque année"})` : "Aucune"],
    ["Loyer charges comprises", money(c.rent_cents + c.charges_cents)],
    ["Dépôt de garantie", c.deposit_cents ? money(c.deposit_cents) : "Aucun"],
    ["Disponible", c.available_on ? `à partir du ${date(c.available_on)}` : "Dès que possible"],
    ["Type de location", regimeLabel(c.lease_regime)],
  ];
  if (p.surface_m2) facts.push(["Surface", `${p.surface_m2.toLocaleString("fr-FR")} m²`]);
  if (p.rooms) facts.push(["Pièces", String(p.rooms)]);
  if (p.dpe_class) facts.push(["Classe énergie (DPE)", p.dpe_class]);
  return (
    <PublicShell wide>
      <article className="location-public stack">
        <header className="stack-sm">
          <p className="eyebrow">{place || "Logement à louer"}</p>
          <h1>{c.title}</h1>
          <p className="muted">
            Proposé par <strong>{landlord.name}</strong> · {LANDLORD_KIND[landlord.kind] ?? "Bailleur"}
          </p>
          <LandlordVerification propertyId={p.id} />
        </header>
        {!c.accepting ? (
          <Notice tone="attention" title="Cette campagne ne reçoit plus de candidatures">
            {c.status === "selection" ? "Le bailleur examine les dossiers déjà reçus." : "Le logement a été attribué ou l’annonce est close."}
          </Notice>
        ) : null}
        {photos.length ? (
          <ul className="list-plain location-gallery" aria-label="Photos du logement">
            {photos.map((ph, i) => (
              <li key={ph.id}>
                <img src={ph.url} alt={ph.caption || `Photo ${i + 1} du logement`} loading={i ? "lazy" : "eager"} />
                {ph.caption ? <span className="small muted">{ph.caption}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="grid-2">
          <section className="stack-sm">
            <h2>Conditions</h2>
            <KeyValues items={facts} />
            {c.description ? <p className="location-description">{c.description}</p> : null}
          </section>
          <section className="card stack-sm">
            <h2>Candidater</h2>
            {c.accepting ? (
              <>
                <p>Constituez votre dossier en ligne : vous, les personnes qui louent avec vous et vos garants. Vous pouvez l’enregistrer et le compléter avant de le déposer.</p>
                <ErrorBox error={error} />
                {user ? (
                  <Button busy={busy} onClick={apply}>
                    Déposer ma candidature
                  </Button>
                ) : (
                  <div className="stack-sm">
                    <p className="small">Un compte est nécessaire pour protéger votre dossier et le retrouver.</p>
                    <Link className="bv-btn bv-btn-primaire" to={`/inscription?suite=${suite}`}>
                      Déposer ma candidature
                    </Link>
                    <Link className="small" to={`/connexion?suite=${suite}`}>
                      J’ai déjà un compte
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <p className="muted">Les candidatures sont fermées pour ce logement.</p>
            )}
            {user ? (
              <p className="small">
                <Link to="/moi/candidatures">Mes candidatures</Link>
              </p>
            ) : null}
          </section>
        </div>
        <PublicCriteria slug={slug} />
        <section className="stack-sm">
          <h2>Pièces qui vous seront demandées</h2>
          <p className="small">Selon votre situation, parmi la liste fixée par le décret n° 2015-1437. Pour chaque garant, des pièces du même type.</p>
          <ul className="location-pieces">
            {LEGAL_PIECES.map((g) => (
              <li key={g.title}>
                <strong>{g.title}</strong> — {g.items.join(" ")}
              </li>
            ))}
          </ul>
          <p className="small">{FORBIDDEN_PIECES}</p>
        </section>
        <section className="stack-sm">
          <h2>Ce qui sera vérifié, et pourquoi</h2>
          <p>{CHECKS_EXPLAINED}</p>
          <p className="small muted">Vos données servent uniquement à l’examen de votre candidature pour ce logement ; elles ne sont ni revendues ni partagées avec d’autres bailleurs.</p>
        </section>
        <p className="small">
          <button type="button" className="location-linkbtn" onClick={() => setReport(true)}>
            Signaler cette annonce
          </button>
        </p>
      </article>
      <ReportModal slug={slug} open={report} onClose={() => setReport(false)} />
    </PublicShell>
  );
}

/** Lien court /c/:code → page de candidature. */
export function ShortLinkPage() {
  const { code = "" } = useParams();
  const q = useQuery({ queryKey: ["lien-court", code], queryFn: () => get<{ slug: string }>(`/api/v1/public/c/${encodeURIComponent(code)}`), retry: false });
  if (q.data) return <Navigate to={`/candidature/${q.data.slug}?via=court`} replace />;
  return (
    <PublicShell>
      {q.isLoading ? (
        <Loading label="Ouverture de l’annonce…" />
      ) : (
        <>
          <h1>Lien introuvable</h1>
          <p>{q.error instanceof ApiError ? q.error.message : "Ce lien de candidature n’existe pas."}</p>
          <Link to="/">Revenir à l’accueil</Link>
        </>
      )}
    </PublicShell>
  );
}

type InvitationInfo = {
  role: string;
  role_label: string;
  first_name: string;
  guarantor_of_name: string | null;
  inviter_name: string;
  email_masked: string;
  campaign_title: string;
  property_label: string;
  place: string;
  landlord_name: string;
};

/** Invitation d'un cotitulaire ou d'un garant à rattacher son compte au dossier. */
export function ApplicantInvitationPage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const { user } = useSession();
  const q = useQuery({ queryKey: ["invitation-candidature", token], queryFn: () => get<InvitationInfo>(`/api/v1/public/applicant-invitations/${token}`), retry: false });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const here = encodeURIComponent(`/candidature/invitation/${token}`);
  return (
    <PublicShell>
      <h1>Compléter une candidature</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="stack">
          <p>
            <strong>{q.data.inviter_name}</strong> vous a ajouté comme{" "}
            <strong>{q.data.role === "garant" ? (q.data.guarantor_of_name ? `garant de ${q.data.guarantor_of_name}` : "garant du foyer") : q.data.role_label.toLowerCase()}</strong> dans sa candidature pour « {q.data.campaign_title} »
            {q.data.place ? ` (${q.data.place})` : ""}, proposé par {q.data.landlord_name}.
          </p>
          <Notice>
            En rattachant votre compte, vous complétez vous-même votre partie : situation, revenus et pièces. Cela ne vous engage pas : un éventuel acte de caution se signe plus tard, avec le bail. Vous pouvez quitter le dossier avant son dépôt.
          </Notice>
          <p className="small muted">Invitation adressée à {q.data.email_masked}. Utilisez un compte avec cette adresse.</p>
          <ErrorBox error={error} />
          {user ? (
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await post<{ application_id: string }>(`/api/v1/me/applicant-invitations/${token}/accept`, {});
                  nav(`/moi/candidatures/${r.application_id}`);
                } catch (err) {
                  setError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Rattacher mon compte au dossier
            </Button>
          ) : (
            <p className="row">
              <Link className="bv-btn bv-btn-primaire" to={`/inscription?suite=${here}`}>
                Créer mon compte
              </Link>
              <Link className="bv-btn bv-btn-discret" to={`/connexion?suite=${here}`}>
                Se connecter
              </Link>
            </p>
          )}
        </div>
      ) : null}
    </PublicShell>
  );
}
